'use client';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef } from 'react';

import DiscordIcon from '@/assets/svg/discord-icon';
import { SSO_BTN_DISCORD } from '@/constants/sso-button-classes';
import { beginDiscordAuthorize } from '@/utils/discord-oauth';
import { stashSsoTrialContext } from '@/utils/sso-trial-context';

import type { SsoButtonProps } from './types';

export default function DiscordSsoButton({ onAuthStart, desktopRedirectUri, autoStart, trialContext }: SsoButtonProps) {
  const { t } = useTranslation('common');
  const router = useRouter();

  const handleClick = useCallback(() => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    // Written on EVERY authorize, null included: the key outlives an abandoned consent screen,
    // so a flow that does not clear it would inherit the previous flow's trial.
    stashSsoTrialContext(trialContext ?? null);
    // Desktop handoff (RFC 8252): carries the validated redirect_uri through the OAuth `state`
    // so src/pages/auth/discord/callback.tsx can hand the login-token to the loopback listener
    // once Discord redirects back.
    if (desktopRedirectUri) {
      beginDiscordAuthorize({ mode: 'desktop', returnTo, locale: router.locale, desktopRedirectUri, onBeforeRedirect: onAuthStart });
      return;
    }
    beginDiscordAuthorize({ mode: 'login', returnTo, locale: router.locale, onBeforeRedirect: onAuthStart });
  }, [onAuthStart, router.locale, desktopRedirectUri, trialContext]);

  // Desktop `?provider=discord` entry: start the redirect flow once on mount. Discord is a
  // full-page navigation, so no user gesture is needed and the button is never actually seen.
  const hasAutoStarted = useRef(false);
  useEffect(() => {
    if (autoStart && !hasAutoStarted.current) {
      hasAutoStarted.current = true;
      handleClick();
    }
  }, [autoStart, handleClick]);

  return (
    <button type='button' onClick={handleClick} className={SSO_BTN_DISCORD}>
      <DiscordIcon className='size-5' />
      {t('login.continue_with_discord')}
    </button>
  );
}
